import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import { useBugoHook } from '@shared/api/bugo/bugo.hook';
import { bugoStateSelectorFamily } from '@shared/api/bugo/bugo.selector';
import {
  giftItemListForStoreDataAtomFamily,
  giftItemListForStoreStatusAtomFamily,
} from '@shared/api/giftItem/giftItem.atom';
import { useGiftItemListForStoreHook } from '@shared/api/giftItem/giftItem.hook';
import { GiftItemInfoType } from '@shared/api/giftItemInfo/giftItemInfo.interface';
import {
  GiftOrderCreateDto,
  GiftOrderItemCreateDto,
  GiftOrderItemType,
} from '@shared/api/giftOrder/giftOrder.interface';
import { TextField } from '@shared/components/TextField';
import { CheckOutState } from '@shared/interfaces/bootpay.interface';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { formatPrice } from '@shared/utils/formatPrice';
import { phoneNumberStringFormator } from '@shared/utils/phoneNumberStringFormator';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { GiftCallHelperBox } from '../components/GiftCallHelperBox';
import { GiftPrivacyNotice } from '../components/GiftPrivacyNotice';
import { giftStoreNavBarTitleAtom } from '../containers/GiftStoreNavBar/giftStoreNavBar.atom';
import { useGiftCheckoutPayment } from '../hooks/useGiftCheckoutPayment';
import { giftCartAtom } from '../state/giftCart.atom';

const GiftCheckoutPage: FC = function GiftCheckoutPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bugoId = searchParams.get('bugoId');

  const setTitle = useSetRecoilState(giftStoreNavBarTitleAtom);
  const [cartItems, setCartItems] = useRecoilState(giftCartAtom);

  // bugo 데이터 조회
  useBugoHook(bugoId ?? '', !bugoId);
  const { data: bugoData } = useRecoilValue(bugoStateSelectorFamily(bugoId ?? ''));

  // 상품 목록 조회 (장바구니 아이템의 상세 정보를 위해)
  useGiftItemListForStoreHook(bugoId ?? '');
  const giftItemListStatus = useRecoilValue(
    giftItemListForStoreStatusAtomFamily(bugoId ?? ''),
  );
  const giftItemListData = useRecoilValue(
    giftItemListForStoreDataAtomFamily(bugoId ?? ''),
  );

  useEffect(() => {
    setTitle('주문/결제');
  }, [setTitle]);

  // 현재 bugoId에 해당하는 장바구니 아이템만 필터링
  const filteredCartItems = useMemo(() => {
    if (!bugoId) return cartItems;
    return cartItems.filter((item) => item.bugoId === bugoId);
  }, [cartItems, bugoId]);

  // 수령인 정보 (member 정보 기반)
  const [receiverName, setReceiverName] = useState('');
  const [receiverTel, setReceiverTel] = useState('');

  // member 정보로 초기값 설정
  useEffect(() => {
    if (bugoData?.member) {
      setReceiverName(bugoData.member.fullName || '');
      setReceiverTel(bugoData.member.phoneNumber || '');
    }
  }, [bugoData]);

  // 수량 증가
  const increaseQuantity = useCallback(
    (itemId: string, orderUnit: number) => {
      setCartItems((prev) =>
        prev.map((item) =>
          item.itemId === itemId
            ? { ...item, quantity: item.quantity + orderUnit }
            : item,
        ),
      );
    },
    [setCartItems],
  );

  // 수량 감소
  const decreaseQuantity = useCallback(
    (itemId: string, minOrderQuantity: number, orderUnit: number) => {
      setCartItems((prev) =>
        prev.map((item) => {
          if (item.itemId === itemId) {
            const newQty = item.quantity - orderUnit;
            return newQty >= minOrderQuantity ? { ...item, quantity: newQty } : item;
          }
          return item;
        }),
      );
    },
    [setCartItems],
  );

  // 총 금액 계산
  const totalPrice = useMemo(() => {
    return filteredCartItems.reduce(
      (sum, item) => sum + item.priceRetail * item.quantity,
      0,
    );
  }, [filteredCartItems]);

  // GiftOrderCreateDto 생성 함수
  const createOrderDto = useCallback((): GiftOrderCreateDto | null => {
    if (!bugoData || filteredCartItems.length === 0 || giftItemListStatus !== 'success') {
      return null;
    }

    // 장바구니 아이템을 orderDetails로 변환
    const orderDetails: GiftOrderItemCreateDto[] = filteredCartItems.map((cartItem) => {
      // 상품 상세 정보 조회
      const giftItemDetail = giftItemListData?.find(
        (item) => item._id === cartItem.itemId,
      );
      const isCouponType = giftItemDetail?.giftItemInfo?.type === GiftItemInfoType.Coupon;

      const orderDetail: GiftOrderItemCreateDto = {
        giftItem: cartItem.itemId,
        quantity: cartItem.quantity,
        type: isCouponType ? GiftOrderItemType.Coupon : GiftOrderItemType.Delivery,
      };

      // 배송형일 때만 배송 정보 추가
      if (!isCouponType) {
        orderDetail.deliveryDetail = {
          receiverAddress: '',
          receiverPostalCode: '',
        };
      }

      return orderDetail;
    });

    return {
      bugo: bugoData._id,
      receiverName,
      receiverPhoneNumber: receiverTel,
      orderDetails,
      paymentDetail: {
        paymentByCash: totalPrice,
        paymentByPoint: 0,
      },
      webBaseUrl: process.env.REACT_APP_WEB_BASE_URL,
    };
  }, [
    bugoData,
    filteredCartItems,
    giftItemListStatus,
    giftItemListData,
    receiverName,
    receiverTel,
    totalPrice,
  ]);

  // 결제 성공 시 장바구니 비우기
  const handlePaymentSuccess = useCallback(() => {
    const purchasedItemIds = filteredCartItems.map((item) => item.itemId);
    setCartItems((prev) =>
      prev.filter((item) => !purchasedItemIds.includes(item.itemId)),
    );
  }, [filteredCartItems, setCartItems]);

  // 결제 훅 사용
  const { checkoutState, handlePayment, isProcessing } = useGiftCheckoutPayment({
    createOrderDto,
    onPaymentSuccess: handlePaymentSuccess,
    bugoId,
    receiverName,
    receiverTel,
    hasItems: filteredCartItems.length > 0,
  });

  // 장바구니가 비어있으면 리다이렉트
  useEffect(() => {
    if (filteredCartItems.length === 0 && checkoutState === CheckOutState.IDLE) {
      // 약간의 딜레이 후 리다이렉트 (초기 로딩 시 오작동 방지)
      const timer = setTimeout(() => {
        if (filteredCartItems.length === 0) {
          navigate(`/gift-store/cart?bugoId=${bugoId}`);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [filteredCartItems, navigate, bugoId, checkoutState]);

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* 1. 수령인 정보 */}
      <div className="bg-white p-4">
        <h3 className="mb-3 font-bold">수령인 정보</h3>
        <div className="space-y-3">
          <TextField
            scrollOnFocus={true}
            className="w-full"
            placeholder="수령인 성함"
            value={receiverName}
            onChange={(e) => setReceiverName(e.target.value)}
          />
          <TextField
            scrollOnFocus={true}
            type="tel"
            className="w-full"
            placeholder="휴대전화번호"
            value={phoneNumberStringFormator(receiverTel)}
            maxLength={13}
            onChange={(e) => setReceiverTel(e.target.value.replace(/[^0-9]/gi, ''))}
          />
        </div>
      </div>

      {/* 2. 구매 상품 */}
      <div className="mt-2 bg-white p-4">
        <h3 className="mb-3 font-bold">
          구매 상품 {filteredCartItems.length > 1 && `(${filteredCartItems.length}개)`}
        </h3>
        <div className="space-y-3">
          {filteredCartItems.map((item) => (
            <div key={item.itemId} className="flex gap-3 border-b pb-3 last:border-b-0">
              {/* 상품 이미지 */}
              <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded bg-gray-100">
                {item.itemImage ? (
                  <img
                    src={fileBucketUrl(item.itemImage, 'w200')}
                    alt={item.itemName}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-xs text-gray-400">
                    No Image
                  </div>
                )}
              </div>

              {/* 상품 정보 */}
              <div className="flex-1">
                <p className="text-sm font-medium">{item.itemName}</p>
                <p className="mt-0.5 text-sm font-bold">
                  {formatPrice(item.priceRetail)}원
                </p>

                {/* 수량 조절 */}
                <div className="mt-2 flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() =>
                      decreaseQuantity(item.itemId, item.minOrderQuantity, item.orderUnit)
                    }
                    disabled={item.quantity <= item.minOrderQuantity}
                    className="flex h-7 w-7 items-center justify-center rounded border bg-gray-50 text-sm font-bold disabled:text-gray-300"
                  >
                    −
                  </button>
                  <span className="min-w-[32px] text-center text-sm font-bold">
                    {item.quantity}
                  </span>
                  <button
                    type="button"
                    onClick={() => increaseQuantity(item.itemId, item.orderUnit)}
                    className="flex h-7 w-7 items-center justify-center rounded border bg-gray-50 text-sm font-bold"
                  >
                    +
                  </button>
                  {item.orderUnit > 1 && (
                    <span className="ml-1 text-[10px] text-gray-400">
                      ({item.orderUnit}개 단위)
                    </span>
                  )}
                </div>
              </div>

              {/* 상품별 금액 */}
              <div className="text-right">
                <p className="text-sm font-bold text-red-500">
                  {formatPrice(item.priceRetail * item.quantity)}원
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 3. 최종 결제 금액 */}
      <div className="mt-2 bg-white p-4">
        <h3 className="mb-3 font-bold">결제 금액</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">상품 금액</span>
            <span>{formatPrice(totalPrice)}원</span>
          </div>
          <div className="flex justify-between border-t pt-2">
            <span className="font-bold">총 결제 금액</span>
            <span className="text-lg font-bold text-red-500">
              {formatPrice(totalPrice)}원
            </span>
          </div>
        </div>
      </div>

      {/* 4. 개인정보 동의 (체크 없이 표시만) */}
      <div className="mt-2 bg-white p-4">
        <GiftPrivacyNotice />
      </div>

      {/* 5. 결제 도움 안내 */}
      <div className="mt-2 bg-white">
        <GiftCallHelperBox />
      </div>

      {/* 6. 하단 고정 결제 버튼 */}
      <div className="fixed bottom-0 left-0 right-0 border-t bg-white p-3 shadow-lg">
        <div className="mx-auto max-w-lg">
          <button
            type="button"
            onClick={handlePayment}
            disabled={filteredCartItems.length === 0 || isProcessing}
            className="w-full rounded-lg bg-red-500 py-3 text-lg font-bold text-white disabled:bg-gray-300"
          >
            {isProcessing ? '결제 진행 중...' : `${formatPrice(totalPrice)}원 결제하기`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GiftCheckoutPage;
